/**
 * Community detail panel (W.2) — the operator-facing read + edit surface for the
 * W.1 Wellness & Headcount keystone object (operator-cop-doctrine §2.3 Municipal
 * EOC, §2.6 elected-official human-impact rollup, §4.4/§4.5 flood + ice-storm
 * doctrine). Communities are AMBIENT (§3B.8): this panel is where the operator
 * actually works one — declares a headcount, runs a wellness check.
 *
 * Sections: identity + population (with the W.1 census/estimate footnotes) →
 * latest headcount card → latest wellness card → linked incident (fly-to) →
 * action buttons (declare headcount / start|update wellness) → history accordion.
 *
 * On any edit it refetches detail + history and calls `onChanged(id)` so the map
 * marker recolours to the new wellness status (the App-level status cache).
 */
import { useEffect, useState } from 'react';
import type {
  CommunityDetail,
  CommunityHeadcount,
  CommunityWellnessCheck,
  ArrivalAttestationsResponse,
} from '@tsr/shared-types';
import {
  CIMD_DIMENSION_KEYS,
  CIMD_DIMENSION_LABEL,
  CIMD_PROVENANCE_LABEL,
  formatVulnerabilityPercent,
  communityTypeToIssuingAuthorityType,
  isFirstNationEvacuation,
  FN_EVAC_REQUEST_ACTION_LABEL,
  FN_EVAC_ABSENT_LABEL,
} from '@tsr/shared-types';
import type { CommunityFeature } from './api';
import {
  fetchCommunityDetail,
  fetchCommunityHeadcounts,
  fetchCommunityWellnessChecks,
  fetchCommunityEvacOrders,
  fetchArrivalAttestations,
  setCommunityRetired,
} from './api';
import { getSessionId } from './lib/operatorActions';
import { showToast } from './lib/toast';
import { communityStatusStyle } from './styles/communityStatus';
import { cimdQuintileStyle } from './styles/cimd';
import { hasEvacuationEvidence } from './EvacuationBody';
import { HeadcountEditorForm } from './HeadcountEditorForm';
import { WellnessCheckEditorForm } from './WellnessCheckEditorForm';
import { IncidentManagementSection } from './components/IncidentManagementSection';
import { CommunityEntityForm } from './CommunityEntityForm';
import { ConnectedEvacOrders } from './ConnectedEvacOrders';
import { FlythroughButton } from './FlythroughButton';
import { CommunityCommsThread } from './CommunityCommsThread';
import { NotificationSubscribeSection } from './NotificationSubscribeSection';

const COMMUNITY_TYPE_LABEL: Record<string, string> = {
  municipality: 'Municipality',
  first_nation: 'First Nation',
  unincorporated: 'Unincorporated',
  metis_settlement: 'Métis Settlement',
  inuit_community: 'Inuit Community',
  other: 'Community',
};

function formatStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d
    .toISOString()
    .replace('T', ' ')
    .replace(/\.\d+Z$/, 'Z');
}

type ModalKind = 'headcount' | 'wellness-start' | 'wellness-update' | 'edit-entity' | null;

interface LinkedIncident {
  id: string;
  number: string;
  lat: number;
  lon: number;
  hazard_type: string | null;
  /** §4.30 — linked-community count, gating the Evacuation entry (data-driven). */
  linked_community_count: number | null;
}

/** Best-effort fetch of an incident's number + centroid (+ hazard + linked-
 *  community count) for the linked-incident fly-to / Evacuation entry. Returns
 *  null on any failure. */
async function fetchLinkedIncident(
  id: string,
  signal: AbortSignal,
): Promise<LinkedIncident | null> {
  try {
    const res = await fetch(`/api/incidents/${id}`, { signal });
    if (!res.ok) return null;
    const inc = (await res.json()) as {
      id?: string;
      incident_number?: string;
      lat?: number;
      lon?: number;
      hazard_type?: string;
      linked_community_count?: number;
    };
    if (!inc.incident_number || inc.lat == null || inc.lon == null) return null;
    return {
      id,
      number: inc.incident_number,
      lat: inc.lat,
      lon: inc.lon,
      hazard_type: inc.hazard_type ?? null,
      linked_community_count: inc.linked_community_count ?? null,
    };
  } catch {
    return null;
  }
}

function StatusBadge({ status }: { status: CommunityWellnessCheck['status'] }): JSX.Element {
  const s = communityStatusStyle(status);
  return (
    <span
      className="inline-block px-1.5 py-0.5 rounded text-[10px] font-medium"
      style={{ backgroundColor: `${s.color}22`, color: s.color }}
      data-testid="community-wellness-status-badge"
    >
      {s.label}
    </span>
  );
}

export function CommunityBody({
  community,
  onChanged,
  onFlyToIncident,
  onOpenEvacuation,
  onMoveCommunity,
  onRecordEvacOrder,
  onViewAsCommunity,
  onEntityChanged,
  onRetired,
  onOpenSitrep,
  onFlythrough,
  flythroughActive = false,
  onOpenReplay,
  aarReplayActive = false,
}: {
  community: CommunityFeature;
  /** Called with the community id after a headcount/wellness edit so the parent
   *  refreshes its status cache + recolours the map marker. */
  onChanged?: (id: string) => void;
  onFlyToIncident?: (lat: number, lon: number) => void;
  /** §4.4 — open the incident-scoped Evacuation Logistics panel for the linked
   *  incident (shown on flood / ice / multi-hazard incidents). */
  onOpenEvacuation?: (incidentId: string) => void;
  /** W.2 editor — start the place-a-point flow to re-place this community's
   *  centroid (App owns the map tool + the geography PATCH). */
  onMoveCommunity?: (community: CommunityFeature) => void;
  /** #445-(3) — start the draw→declare flow for an operator evacuation order,
   *  pre-seeding this community as the named issuing authority (App owns the draw
   *  tool + the declare POST). */
  onRecordEvacOrder?: (preseed: {
    communityId: string;
    communityName: string;
    communityType: string;
  }) => void;
  /** #458 (role-view council §3) — enter the PEOC "view as <community>" transient
   *  municipal lens: glance at this community's municipal COP before tasking /
   *  briefing. App owns the snapshot/apply/restore; this is a read-only lens. */
  onViewAsCommunity?: (community: CommunityFeature) => void;
  /** W.2 editor — called after an identity/population edit so the parent re-fetches
   *  the map layer (the marker label/name changed, not just its colour). */
  onEntityChanged?: (id: string) => void;
  /** W.2 editor — called after a soft-retire so the parent refreshes the layer +
   *  closes the panel. */
  onRetired?: (id: string) => void;
  /** SITL surface — open a SITREP from the linked incident's report chain in the
   *  existing 720px document modal (§4.19.x). */
  onOpenSitrep?: (id: string) => void;
  /** #179 — start/stop the operator 3D flythrough around this community. */
  onFlythrough?: (target: { lon: number; lat: number; label?: string }) => void;
  /** #179 — whether a flythrough is running (drives the button label/state). */
  flythroughActive?: boolean;
  /** #180 — enter the AAR time replay for the linked incident. */
  onOpenReplay?: (incidentId: string) => void;
  aarReplayActive?: boolean;
}): JSX.Element {
  const id = community.id;
  const p = community.properties;
  const [detail, setDetail] = useState<CommunityDetail | null>(null);
  const [headcounts, setHeadcounts] = useState<CommunityHeadcount[]>([]);
  const [wellness, setWellness] = useState<CommunityWellnessCheck[]>([]);
  const [linked, setLinked] = useState<LinkedIncident | null>(null);
  // #645 — the HOST arrival-attestation registers (as_host / as_sending), shown BESIDE the
  // community's own headcount. The two reconcile to NOTHING (a disagreement is a FACT).
  const [attestations, setAttestations] = useState<ArrivalAttestationsResponse | null>(null);
  const [modal, setModal] = useState<ModalKind>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [confirmingRetire, setConfirmingRetire] = useState(false);
  const [retiring, setRetiring] = useState(false);
  // #637 — for a First Nation community: how many active evacuation REQUESTS cover it, to
  // drive the honest absent state. null = not applicable / not yet resolved.
  const [fnCoveringEvacCount, setFnCoveringEvacCount] = useState<number | null>(null);

  // Load detail + history (re-runs after an edit via reloadNonce).
  useEffect(() => {
    const ctl = new AbortController();
    void fetchCommunityDetail(id, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    void fetchCommunityHeadcounts(id, ctl.signal)
      .then(setHeadcounts)
      .catch(() => undefined);
    void fetchCommunityWellnessChecks(id, ctl.signal)
      .then(setWellness)
      .catch(() => undefined);
    void fetchArrivalAttestations(id, ctl.signal)
      .then(setAttestations)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [id, reloadNonce]);

  // Resolve the linked incident (from the latest headcount) for the fly-to.
  useEffect(() => {
    const incidentId = detail?.latest_headcount?.incident_id;
    if (!incidentId) {
      setLinked(null);
      return undefined;
    }
    const ctl = new AbortController();
    void fetchLinkedIncident(incidentId, ctl.signal).then(setLinked);
    return () => ctl.abort();
  }, [detail?.latest_headcount?.incident_id]);

  // #637 — for a First Nation community, resolve whether any active evacuation REQUEST covers
  // it, so the panel can render the honest absent state ("No evacuation request recorded")
  // instead of an all-clear. FN-only — municipal communities keep their existing behaviour
  // (ConnectedEvacOrders renders nothing when empty). Re-runs after an in-panel edit.
  useEffect(() => {
    if (!isFirstNationEvacuation(communityTypeToIssuingAuthorityType(p.community_type))) {
      setFnCoveringEvacCount(null);
      return undefined;
    }
    const ctl = new AbortController();
    void fetchCommunityEvacOrders(id, ctl.signal)
      .then((r) => setFnCoveringEvacCount(r.orders.length))
      .catch(() => undefined);
    return () => ctl.abort();
  }, [id, p.community_type, reloadNonce]);

  const afterEdit = (): void => {
    setModal(null);
    setReloadNonce((n) => n + 1);
    onChanged?.(id);
  };

  // After an identity/population edit: refetch the panel + tell the parent so the
  // map layer re-fetches (the marker's name/label changed, not just its colour).
  const afterEntityEdit = (): void => {
    setModal(null);
    setReloadNonce((n) => n + 1);
    onEntityChanged?.(id);
  };

  const retire = (): void => {
    if (retiring) return;
    setRetiring(true);
    void setCommunityRetired(id, true, getSessionId())
      .then(() => {
        showToast(`${p.name} retired.`, { variant: 'success' });
        onRetired?.(id);
      })
      .catch(() => showToast('Could not retire the community. Please retry.', { variant: 'error' }))
      .finally(() => {
        setRetiring(false);
        setConfirmingRetire(false);
      });
  };

  const latestHc = detail?.latest_headcount ?? null;
  const latestWc = detail?.latest_wellness_check ?? null;
  const activeWc = latestWc && latestWc.status !== 'complete' ? latestWc : null;
  // Prefer the refetched detail (authoritative; reflects an in-panel identity /
  // population edit) over the map feature's props, which can be stale post-edit.
  const dName = detail?.name ?? p.name;
  const dType = detail?.community_type ?? p.community_type;
  const dJurisdiction = detail ? detail.jurisdiction : p.jurisdiction;
  const dPopulation = detail ? detail.population : p.population;
  const dVulnerable = detail ? detail.vulnerable_population : p.vulnerable_population;
  const typeLabel = COMMUNITY_TYPE_LABEL[dType] ?? 'Community';
  // #637 — a First Nation evacuation is a REQUEST to the PEOC, not an order issued by the
  // community. Reframes the record affordance + its absent state.
  const isFirstNation = isFirstNationEvacuation(communityTypeToIssuingAuthorityType(dType));
  const meta = (detail?.metadata ?? {}) as Record<string, unknown>;
  const popSource = typeof meta.population_source === 'string' ? meta.population_source : null;
  const vulnEstimate = meta.vulnerable_population_estimate === true;
  const note = typeof meta.note === 'string' ? meta.note : null;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="community-body">
      {/* Identity */}
      <div className="border-l-2 border-sky-400 pl-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Community</p>
        <h2 className="text-base font-semibold">{dName}</h2>
        <div className="flex items-center gap-2 mt-1">
          <span className="px-1.5 py-0.5 rounded bg-slate-700/70 text-slate-200 text-[10px]">
            {typeLabel}
          </span>
          {dJurisdiction && <span className="text-xs text-slate-400">{dJurisdiction}</span>}
        </div>
      </div>

      {/* Population & vulnerability (#234, superseding #189's derived band) —
          population + vulnerable counts, the StatCan CIMD 2021 quintiles (the
          government's own bands; colour always paired with the quintile text,
          Law 1; unknown is honest, not hidden), and the operator-entered share
          as the clearly-labelled live complement. */}
      <section data-testid="community-vulnerability-section">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
          Population & vulnerability
        </h3>
        <div className="rounded border border-slate-700 bg-slate-800/40 p-2.5 space-y-1.5">
          <div className="grid grid-cols-2 gap-1 text-center">
            <div>
              <div className="font-mono text-base text-slate-100">
                {dPopulation != null ? dPopulation.toLocaleString() : '—'}
              </div>
              <div className="text-[9px] uppercase tracking-wide text-slate-500">Total</div>
            </div>
            <div>
              <div className="font-mono text-base text-slate-100">
                {dVulnerable != null ? dVulnerable.toLocaleString() : '—'}
              </div>
              <div className="text-[9px] uppercase tracking-wide text-slate-500">Vulnerable</div>
            </div>
          </div>
          {(() => {
            // Full CIMD block rides the detail fetch; the map feature carries
            // the headline quintile so the chip renders before detail loads.
            const headlineQ = detail
              ? detail.cimd_situational_vulnerability_q
              : p.cimd_situational_vulnerability_q;
            const hs = cimdQuintileStyle(headlineQ);
            const queried = detail ? detail.cimd_enriched_at !== null : headlineQ !== null;
            return (
              <>
                <div
                  className="flex items-center gap-2 pt-1 border-t border-slate-700/60"
                  data-testid="community-cimd-headline-chip"
                  title="StatCan CIMD 2021, dissemination-area level. Quintiles are national bands: 1 = least deprived, 5 = most deprived. Headline = Situational Vulnerability, the User Guide's emergency-management dimension."
                >
                  <span
                    aria-hidden
                    className="inline-block w-2 h-2 rounded-full"
                    style={{ backgroundColor: hs.color }}
                  />
                  <span className="text-[11px] uppercase tracking-wide" style={{ color: hs.color }}>
                    Situational vulnerability: {hs.label}
                    {hs.descriptor ? ` — ${hs.descriptor}` : ''}
                  </span>
                </div>
                {detail && (
                  <div className="space-y-0.5" data-testid="community-cimd-dimensions">
                    {CIMD_DIMENSION_KEYS.map((key) => {
                      const q = detail[`cimd_${key}_q` as const];
                      const s = cimdQuintileStyle(q);
                      return (
                        <div key={key} className="flex items-center justify-between text-[11px]">
                          <span className="text-slate-400">{CIMD_DIMENSION_LABEL[key]}</span>
                          <span className="uppercase tracking-wide" style={{ color: s.color }}>
                            {s.quintile !== null ? s.label : '—'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="text-[10px] text-slate-500" data-testid="community-cimd-provenance">
                  {queried
                    ? headlineQ !== null || (detail && detail.cimd_dauid !== null)
                      ? `${CIMD_PROVENANCE_LABEL}${detail?.cimd_dauid ? ` · DA ${detail.cimd_dauid}` : ''}`
                      : `${CIMD_PROVENANCE_LABEL}: no published score for this location (suppressed or unscored dissemination area).`
                    : `${CIMD_PROVENANCE_LABEL}: not yet resolved for this community.`}
                </p>
              </>
            );
          })()}
          {(() => {
            // #189's ratio survives ONLY as the operator-entered live-counts
            // complement — plain text, no band, so it can't be confused with
            // the standardized census index above.
            const share =
              dPopulation != null && dPopulation > 0 && dVulnerable != null && dVulnerable >= 0
                ? formatVulnerabilityPercent(Math.min(1, dVulnerable / dPopulation))
                : null;
            return (
              <p
                className="text-[10px] text-slate-400 pt-1 border-t border-slate-700/60"
                data-testid="community-operator-share"
              >
                {share !== null
                  ? `Operator-entered vulnerable share: ${share} of population (live counts, not census).`
                  : 'Operator-entered vulnerable share unavailable — population or vulnerable count missing.'}
              </p>
            );
          })()}
        </div>
        {(popSource || vulnEstimate) && (
          <p
            className="mt-1 text-[10px] text-slate-500 leading-relaxed"
            data-testid="community-pop-footnote"
          >
            {popSource ? `Population ≈ ${popSource.replace(/_/g, ' ')}. ` : ''}
            {vulnEstimate ? 'Vulnerable count is an estimate.' : ''}
          </p>
        )}
        {note && <p className="mt-1 text-[11px] text-amber-200/80 leading-relaxed">{note}</p>}
      </section>

      {/* Latest headcount */}
      <section data-testid="community-headcount-card">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Latest headcount</h3>
        {latestHc ? (
          <div className="rounded border border-slate-700 bg-slate-800/40 p-2.5 space-y-1.5">
            <div className="grid grid-cols-4 gap-1 text-center">
              {(
                [
                  ['Evac', latestHc.evacuated, 'text-rose-300'],
                  ['Remain', latestHc.remaining, 'text-slate-200'],
                  ['Acct', latestHc.accounted_for, 'text-emerald-300'],
                  ['Unreach', latestHc.unreachable, 'text-amber-300'],
                ] as const
              ).map(([label, val, cls]) => (
                <div key={label}>
                  <div className={`font-mono text-sm ${cls}`}>{val.toLocaleString()}</div>
                  <div className="text-[9px] uppercase tracking-wide text-slate-500">{label}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between text-[10px] text-slate-500 pt-1 border-t border-slate-700/60">
              <span>as of {formatStamp(latestHc.as_of)}</span>
              <span className="capitalize">{latestHc.source.replace(/_/g, ' ')}</span>
            </div>
            {/* #645 PIN #9 — awaiting_airlift: NULL is "not reported by <community>", never 0. */}
            <div
              data-testid="community-awaiting-airlift"
              className="text-[10px] text-slate-500 pt-1 border-t border-slate-700/60"
            >
              Awaiting flight:{' '}
              {latestHc.awaiting_airlift == null ? (
                <span className="italic text-slate-500">not reported by {p.name}</span>
              ) : (
                <span className="font-mono text-indigo-300">
                  {latestHc.awaiting_airlift.toLocaleString()}
                </span>
              )}
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500">No headcount recorded yet.</p>
        )}
      </section>

      {/* Latest wellness check */}
      <section data-testid="community-wellness-card">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
          Latest wellness check
        </h3>
        {latestWc ? (
          <div className="rounded border border-slate-700 bg-slate-800/40 p-2.5 space-y-1.5">
            <div className="flex items-center justify-between">
              <StatusBadge status={latestWc.status} />
              <span className="text-[10px] text-slate-500">
                updated {formatStamp(latestWc.last_updated_at)}
              </span>
            </div>
            <dl className="grid grid-cols-2 gap-y-0.5 text-xs">
              <dt className="text-slate-400">Warming centres</dt>
              <dd className="font-mono">{latestWc.warming_centres_active}</dd>
              <dt className="text-slate-400">Contact attempts</dt>
              <dd className="font-mono">{latestWc.contact_attempts_made}</dd>
              <dt className="text-slate-400">Vulnerable contacted</dt>
              <dd className="font-mono">{latestWc.vulnerable_residents_contacted}</dd>
              <dt className="text-slate-400">Vulnerable unreachable</dt>
              <dd className="font-mono text-amber-300">
                {latestWc.vulnerable_residents_unreachable}
              </dd>
            </dl>
          </div>
        ) : (
          <p className="text-xs text-slate-500">No wellness check started yet.</p>
        )}
      </section>

      {/* #645 — HOST arrival attestations, shown BESIDE the community's own headcount. Two
          registers, two authorities, neither overwrites the other; a disagreement is a FACT
          rendered UNRECONCILED, never averaged, never resolved to "accounted for". Absence of
          any host attestation reads "not reported", never 0. Aggregate only — no PII. */}
      {attestations &&
        (attestations.as_sending.length > 0 ||
          attestations.as_host.length > 0 ||
          (latestHc != null && (latestHc.evacuated > 0 || latestHc.awaiting_airlift != null))) && (
          <section data-testid="community-arrival-attestations">
            <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
              Host arrival attestations
            </h3>
            <div className="rounded border border-slate-700 bg-slate-800/40 p-2.5 space-y-2 text-xs">
              {(attestations.as_sending.length > 0 ||
                (latestHc != null && latestHc.evacuated > 0)) && (
                <div className="space-y-1">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">
                    This community&apos;s people, attested at hosts
                  </div>
                  {attestations.total_attested_arrivals == null ? (
                    <p data-testid="attest-sending-absent" className="italic text-slate-500">
                      Not reported by any host — <span className="text-slate-400">not zero</span>
                    </p>
                  ) : (
                    <>
                      <div className="font-mono text-indigo-300" data-testid="attest-sending-total">
                        {attestations.total_attested_arrivals.toLocaleString()} attested across{' '}
                        {attestations.as_sending.length} host
                        {attestations.as_sending.length === 1 ? '' : 's'}
                      </div>
                      {latestHc != null &&
                        latestHc.evacuated !== attestations.total_attested_arrivals && (
                          <p
                            data-testid="attest-unreconciled"
                            className="text-amber-300 text-[11px]"
                          >
                            Unreconciled: this community reports{' '}
                            {latestHc.evacuated.toLocaleString()} evacuated · hosts attest{' '}
                            {attestations.total_attested_arrivals.toLocaleString()} arrived. Both
                            stand — not merged, not reconciled.
                          </p>
                        )}
                      <ul className="space-y-0.5">
                        {attestations.as_sending.map((a) => (
                          <li key={a.id} className="flex justify-between gap-2">
                            <span className="text-slate-300">
                              {a.host_community_name ?? 'host'}
                            </span>
                            <span className="text-slate-400">
                              <span className="font-mono text-indigo-300">
                                {a.persons.toLocaleString()}
                              </span>{' '}
                              · {a.attested_by_agency_name ?? 'agency'} ·{' '}
                              {formatStamp(a.attested_at)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
              {attestations.as_host.length > 0 && (
                <div className="space-y-1 pt-1 border-t border-slate-700/60">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">
                    Arrivals attested here (as host)
                  </div>
                  <ul className="space-y-0.5">
                    {attestations.as_host.map((a) => (
                      <li key={a.id} className="flex justify-between gap-2">
                        <span className="text-slate-300">
                          {a.sending_community_name ?? 'community'}
                        </span>
                        <span className="text-slate-400">
                          <span className="font-mono text-indigo-300">
                            {a.persons.toLocaleString()}
                          </span>{' '}
                          · {formatStamp(a.attested_at)}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-[10px] text-slate-500 pt-1 border-t border-slate-700/60">
                Aggregate arrivals only — no personal or reunification data. A host attestation
                never overwrites this community&apos;s own headcount.
              </p>
            </div>
          </section>
        )}

      {/* #179 — operator 3D flythrough around the community (on-demand
          photoreal tiles; prior view restored when the tour ends). */}
      {onFlythrough && (
        <FlythroughButton
          target={{
            lon: community.geometry.coordinates[0],
            lat: community.geometry.coordinates[1],
            label: p.name,
          }}
          active={flythroughActive}
          onFlythrough={onFlythrough}
        />
      )}

      {/* #458 (role-view council §3) — PEOC "view as <community>" transient lens:
          glance at this community's municipal COP (the right default layer set)
          before tasking / briefing. A read-only lens, restored on exit — never a
          persistent mode (App owns snapshot/apply/restore). */}
      {onViewAsCommunity && (
        <button
          type="button"
          data-testid="community-view-as"
          onClick={() => onViewAsCommunity(community)}
          className="w-full flex items-center justify-center gap-2 py-2 px-3 rounded bg-indigo-600/20 hover:bg-indigo-600/30 border border-indigo-500/50 text-indigo-200 text-sm font-medium"
        >
          <span aria-hidden>👁</span> View as this community
        </button>
      )}

      {/* #206 — evacuation orders whose polygon covers this community (renders only
          when at least one active order does). */}
      <ConnectedEvacOrders source={{ kind: 'community', id }} />

      {/* #637 — a First Nation community with NO active evacuation request shows the honest
          absent state (never an all-clear / "not required"). Only when nothing covers it —
          ConnectedEvacOrders above surfaces an active request. */}
      {isFirstNation && fnCoveringEvacCount === 0 && (
        <p
          data-testid="community-fn-evac-absent"
          className="text-[11px] text-slate-500 border border-slate-800 rounded px-2.5 py-1.5"
        >
          {FN_EVAC_ABSENT_LABEL}
        </p>
      )}

      {/* Linked incident */}
      {linked && (
        <section data-testid="community-linked-incident">
          <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">
            Linked incident
          </h3>
          <div className="flex items-center justify-between rounded border border-slate-700 bg-slate-800/40 px-2.5 py-2">
            <span className="font-mono text-sm">{linked.number}</span>
            <button
              type="button"
              data-testid="community-fly-incident"
              onClick={() => onFlyToIncident?.(linked.lat, linked.lon)}
              className="text-[11px] py-1 px-2 rounded bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-200"
            >
              Fly to incident
            </button>
          </div>
          {/* §4.4 — open the assembled Evacuation Logistics panel for this
              incident (flood / ice / multi-hazard only). */}
          {hasEvacuationEvidence(linked.linked_community_count) && (
            <button
              type="button"
              data-testid="community-open-evacuation"
              onClick={() => onOpenEvacuation?.(linked.id)}
              className="mt-2 w-full flex items-center justify-center gap-2 py-2 px-3 rounded bg-rose-600/90 hover:bg-rose-600 border border-rose-500 text-white text-sm font-medium"
            >
              <span aria-hidden>🚁</span> Evacuation Logistics
            </button>
          )}
          {/* SITL surface (§4.19.x) — incident header + operational-period strip
              + report chain for the linked incident. */}
          <div className="mt-3">
            <IncidentManagementSection
              incidentId={linked.id}
              onOpenSitrep={onOpenSitrep}
              onOpenReplay={onOpenReplay}
              replayActive={aarReplayActive}
            />
          </div>
        </section>
      )}

      {/* Actions */}
      <section className="flex flex-wrap gap-2">
        <button
          type="button"
          data-testid="community-declare-headcount"
          onClick={() => setModal('headcount')}
          className="text-xs py-1.5 px-3 rounded bg-rose-600/90 hover:bg-rose-600 border border-rose-500 text-white font-medium"
        >
          Record Headcount
        </button>
        {activeWc ? (
          <button
            type="button"
            data-testid="community-update-wellness"
            onClick={() => setModal('wellness-update')}
            className="text-xs py-1.5 px-3 rounded bg-sky-600/90 hover:bg-sky-600 border border-sky-500 text-white font-medium"
          >
            Update Wellness Check
          </button>
        ) : (
          <button
            type="button"
            data-testid="community-start-wellness"
            onClick={() => setModal('wellness-start')}
            className="text-xs py-1.5 px-3 rounded bg-amber-600/90 hover:bg-amber-600 border border-amber-500 text-white font-medium"
          >
            Start Wellness Check
          </button>
        )}
        {onRecordEvacOrder && (
          <button
            type="button"
            data-testid="community-record-evac-order"
            onClick={() =>
              onRecordEvacOrder({
                communityId: id,
                communityName: dName,
                communityType: dType,
              })
            }
            className="text-xs py-1.5 px-3 rounded bg-violet-700/90 hover:bg-violet-700 border border-violet-500 text-white font-medium"
          >
            {isFirstNation
              ? FN_EVAC_REQUEST_ACTION_LABEL
              : `Record evacuation order — issued by ${dName}`}
          </button>
        )}
      </section>

      {/* Manage community entity (W.2 editor) — edit identity/population, move the
          centroid on the map, or soft-retire (no hard delete). */}
      <section className="border-t border-slate-800 pt-3" data-testid="community-manage">
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Manage community</h3>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="community-edit-entity"
            onClick={() => setModal('edit-entity')}
            className="text-xs py-1.5 px-3 rounded bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-100 font-medium"
          >
            Edit details
          </button>
          <button
            type="button"
            data-testid="community-move"
            onClick={() => onMoveCommunity?.(community)}
            className="text-xs py-1.5 px-3 rounded bg-slate-700 hover:bg-slate-600 border border-slate-600 text-slate-100 font-medium"
          >
            Move on map
          </button>
          {confirmingRetire ? (
            <span className="inline-flex items-center gap-1.5">
              <button
                type="button"
                data-testid="community-retire-confirm"
                disabled={retiring}
                onClick={retire}
                className="text-xs py-1.5 px-3 rounded bg-rose-700/90 hover:bg-rose-700 border border-rose-600 text-white font-medium disabled:opacity-50 disabled:cursor-wait"
              >
                {retiring ? 'Retiring…' : 'Confirm retire'}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingRetire(false)}
                className="text-xs py-1.5 px-2 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300"
              >
                Cancel
              </button>
            </span>
          ) : (
            <button
              type="button"
              data-testid="community-retire"
              onClick={() => setConfirmingRetire(true)}
              className="text-xs py-1.5 px-3 rounded bg-slate-800 hover:bg-slate-700 border border-rose-800/70 text-rose-300 font-medium"
            >
              Retire
            </button>
          )}
        </div>
        <p className="mt-1.5 text-[10px] text-slate-500 leading-relaxed">
          Retiring hides the community from the map; its history and audit trail are kept (no
          delete).
        </p>
      </section>

      {/* #420a — PEOC↔community two-way coordination thread (ambient, incident-free).
          The §E sim G2-closer: the Liaison coordinates with the community IN the COP. */}
      <CommunityCommsThread communityId={id} />

      {/* #6a — jurisdiction-scoped SMS opt-in (role-view council §6 awareness gap).
          A not-24/7 municipal operator is paged on a threat to THIS community while
          away from the desk. Consent + STOP are first-class; operational-only. */}
      <NotificationSubscribeSection communityId={id} communityName={dName} />

      {/* History accordion */}
      <section>
        <button
          type="button"
          data-testid="community-history-toggle"
          onClick={() => setHistoryOpen((o) => !o)}
          className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 py-1"
        >
          <span>History</span>
          <span className="text-slate-500">
            {historyOpen ? '▾' : '▸'} {headcounts.length + wellness.length}
          </span>
        </button>
        {historyOpen && (
          <div
            className="mt-1.5 space-y-3 max-h-64 overflow-y-auto"
            data-testid="community-history"
          >
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Headcounts</p>
              {headcounts.length === 0 ? (
                <p className="text-[11px] text-slate-600">None</p>
              ) : (
                <ul className="space-y-1">
                  {headcounts.map((h) => (
                    <li
                      key={h.id}
                      className="text-[11px] text-slate-300 flex items-center justify-between gap-2 border-b border-slate-800 pb-1"
                    >
                      <span className="text-slate-500">{formatStamp(h.as_of)}</span>
                      <span className="font-mono">
                        E{h.evacuated} · R{h.remaining} · A{h.accounted_for} · U{h.unreachable}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
                Wellness checks
              </p>
              {wellness.length === 0 ? (
                <p className="text-[11px] text-slate-600">None</p>
              ) : (
                <ul className="space-y-1">
                  {wellness.map((w) => (
                    <li
                      key={w.id}
                      className="text-[11px] flex items-center justify-between gap-2 border-b border-slate-800 pb-1"
                    >
                      <span className="text-slate-500">{formatStamp(w.last_updated_at)}</span>
                      <StatusBadge status={w.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </section>

      {modal === 'headcount' && (
        <HeadcountEditorForm
          community={{ id, name: p.name }}
          incidentId={latestHc?.incident_id ?? null}
          onClose={() => setModal(null)}
          onSubmitted={afterEdit}
        />
      )}
      {modal === 'wellness-start' && (
        <WellnessCheckEditorForm
          community={{ id, name: p.name }}
          incidentId={latestHc?.incident_id ?? null}
          onClose={() => setModal(null)}
          onSubmitted={afterEdit}
        />
      )}
      {modal === 'wellness-update' && activeWc && (
        <WellnessCheckEditorForm
          community={{ id, name: p.name }}
          existing={activeWc}
          onClose={() => setModal(null)}
          onSubmitted={afterEdit}
        />
      )}
      {modal === 'edit-entity' && (
        <CommunityEntityForm
          mode="edit"
          community={{
            id,
            name: dName,
            community_type: dType,
            jurisdiction: dJurisdiction,
            population: dPopulation,
            vulnerable_population: dVulnerable,
          }}
          onClose={() => setModal(null)}
          onSubmitted={afterEntityEdit}
        />
      )}
    </div>
  );
}
